import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  HeartHandshake,
  PhoneCall,
  MessageCircle,
  EyeOff,
  Heart,
  Star,
  CalendarClock,
  Lock,
  LifeBuoy,
  ArrowRight,
  Play,
  Mic,
  MicOff,
  Radio,
  Menu,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LegalFooter from '../components/LegalFooter';
import AperitivoSection from '../components/AperitivoSection';
import LanguageSwitcher from '../components/LanguageSwitcher';
import AndroidPlayBanner from '../components/landing/AndroidPlayBanner';
import { trackPixelEvent } from '../hooks/useMetaPixel';
import { LANDING_STRUCTURE, PLAY_STORE_URL } from '../config/landingContent';
import { LANDING_IMAGES as IMG } from '../config/landingImages';
import { useLandingMetadata } from '../hooks/useLandingMetadata';

/**
 * Nymia · Public Landing (nymia.me) — Restyling Cinematografico
 *
 * CONCEPT: «Le persone prima di tutto.» Non un social network — un luogo
 * dove ritrovare conversazioni autentiche, ascolto vero ed emozioni reali.
 * Palette: nero #050505 · oro Nymia · bianco. Fotografia calda, cinematografica.
 *
 * Copy in /i18n/locales/{it,en}/landing.json · foto in config/landingImages.js
 * · struttura (icone/testid) in config/landingContent.js.
 */

const GOLD = '#D4AF37';

const ICONS = {
  Users, HeartHandshake, MessageCircle, EyeOff,
  Radio, Mic, Lock, LifeBuoy,
};

const easeCine = [0.16, 1, 0.3, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.1, ease: easeCine },
  }),
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// ─── Header ──────────────────────────────────────────────────────────────────
const LandingHeader = ({ onEnter }) => {
  const { t } = useTranslation('landing');
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { key: 'home', to: '/' },
    { key: 'cosa', to: '/cosa-puoi-fare' },
    { key: 'creator', to: '/creator' },
    { key: 'contatti', to: '/contact' },
  ];

  const goHome = () => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? 'border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl' : 'bg-transparent'
      }`}
      data-testid="landing-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <span
            className="text-2xl font-semibold leading-none"
            style={{ fontFamily: "'Playfair Display', serif", color: GOLD }}
          >
            N
          </span>
          <span className="text-sm font-semibold uppercase tracking-[0.35em] text-white">Nymia</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {navLinks.map((l) => (
            l.to === '/' ? (
              <button
                key={l.key}
                onClick={goHome}
                className="text-[13px] font-medium text-white/70 transition-colors hover:text-white"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid={`nav-${l.key}`}
              >
                {t(`nav.${l.key}`)}
              </button>
            ) : (
              <Link
                key={l.key}
                to={l.to}
                className="text-[13px] font-medium text-white/70 transition-colors hover:text-white"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid={`nav-${l.key}`}
              >
                {t(`nav.${l.key}`)}
              </Link>
            )
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher variant="compact" testId="landing-lang" />
          <Link
            to="/login"
            className="rounded-full border border-white/15 px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:border-white/40"
            data-testid="header-login-btn"
          >
            {t('nav.accedi')}
          </Link>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={onEnter}
            className="rounded-full px-5 py-2 text-[13px] font-bold text-black transition-opacity hover:opacity-90"
            style={{ backgroundColor: GOLD }}
            data-testid="header-enter-btn"
          >
            {t('nav.inizia')}
          </motion.button>
        </div>

        <button
          className="flex h-10 w-10 items-center justify-center text-white lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-btn"
          aria-label="menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-white/5 bg-[#050505]/95 px-5 pb-6 pt-3 backdrop-blur-xl lg:hidden" data-testid="mobile-menu">
          {navLinks.map((l) => (
            l.to === '/' ? (
              <button
                key={l.key}
                onClick={goHome}
                className="block w-full py-3 text-left text-sm font-medium text-white/80"
                data-testid={`mobile-nav-${l.key}`}
              >
                {t(`nav.${l.key}`)}
              </button>
            ) : (
              <Link
                key={l.key}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className="block w-full py-3 text-left text-sm font-medium text-white/80"
                data-testid={`mobile-nav-${l.key}`}
              >
                {t(`nav.${l.key}`)}
              </Link>
            )
          ))}
          <div className="mt-4 flex items-center gap-3">
            <Link
              to="/login"
              className="flex-1 rounded-full border border-white/15 py-2.5 text-center text-sm font-semibold text-white"
              data-testid="mobile-login-btn"
            >
              {t('nav.accedi')}
            </Link>
            <button
              onClick={onEnter}
              className="flex-1 rounded-full py-2.5 text-center text-sm font-bold text-black"
              style={{ backgroundColor: GOLD }}
              data-testid="mobile-enter-btn"
            >
              {t('nav.inizia')}
            </button>
          </div>
          <div className="mt-4"><LanguageSwitcher variant="compact" testId="mobile-lang" /></div>
        </div>
      )}
    </header>
  );
};

// ─── Hero ────────────────────────────────────────────────────────────────────
const Hero = ({ onEnter }) => {
  const { t } = useTranslation('landing');
  return (
    <section className="relative min-h-screen overflow-hidden" data-testid="landing-hero">
      {/* Cinematic photo — right side on desktop, full bleed on mobile */}
      <div className="absolute inset-0">
        <img
          src={IMG.hero}
          alt=""
          className="h-full w-full object-cover object-[70%_center]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-7xl items-center px-5 pb-24 pt-28 lg:px-8">
        <div className="max-w-2xl">
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="text-5xl leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="hero-title"
          >
            {t('hero.title_1')}
            <br />
            {t('hero.title_2')}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="mt-6 text-lg font-semibold sm:text-xl"
            style={{ color: GOLD, fontFamily: "'Manrope', sans-serif" }}
            data-testid="hero-subtitle"
          >
            {t('hero.subtitle_1')}
            <br />
            {t('hero.subtitle_2')}
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-6 max-w-lg text-[15px] leading-relaxed text-white/70"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            data-testid="hero-text"
          >
            <p>{t('hero.text')}</p>
            <p className="mt-3">{t('hero.text_2')}</p>
            <p className="mt-4 text-[13px] font-semibold tracking-wide" style={{ color: 'rgba(212,175,55,0.85)' }} data-testid="hero-micro">
              {t('hero.micro')}
            </p>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={onEnter}
              className="rounded-full px-8 py-3.5 text-sm font-bold text-black shadow-[0_0_40px_rgba(212,175,55,0.25)] transition-opacity hover:opacity-90"
              style={{ backgroundColor: GOLD }}
              data-testid="hero-enter-btn"
            >
              {t('hero.cta_primary')}
            </motion.button>
            <button
              onClick={() => scrollToId('esperienza')}
              className="group flex items-center gap-3 text-sm font-semibold text-white/80 transition-colors hover:text-white"
              data-testid="hero-discover-btn"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 transition-colors group-hover:border-white/50">
                <Play className="ml-0.5 h-4 w-4 fill-current" />
              </span>
              {t('hero.cta_secondary')}
            </button>
          </motion.div>
        </div>

        {/* Golden handwritten note */}
        <motion.p
          initial={{ opacity: 0, rotate: -8 }}
          animate={{ opacity: 1, rotate: -6 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute right-8 top-[30%] hidden max-w-[230px] text-right text-3xl leading-snug lg:block"
          style={{ fontFamily: "'Caveat', cursive", color: GOLD }}
          data-testid="hero-note"
        >
          {t('hero.note')}
        </motion.p>
      </div>
    </section>
  );
};

// ─── Perché Nymia (strip) ────────────────────────────────────────────────────
const WhyStrip = () => {
  const { t } = useTranslation('landing');
  const cards = t('why.cards', { returnObjects: true }) || [];
  return (
    <section id="scopri" className="border-y border-white/5 bg-[#0A0A0A]" data-testid="section-why">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-7 px-5 py-12 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
        {LANDING_STRUCTURE.why.cards.map((c, i) => {
          const Icon = ICONS[c.icon];
          const copy = cards[i] || {};
          return (
            <motion.div
              key={c.testid}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              custom={i}
              className="flex items-start gap-4"
              data-testid={c.testid}
            >
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border"
                style={{ borderColor: 'rgba(212,175,55,0.35)', color: GOLD }}
              >
                <Icon className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <div style={{ fontFamily: "'Manrope', sans-serif" }}>
                <h3 className="text-[15px] font-bold text-white">{copy.title}</h3>
                <p className="mt-1 text-[13px] leading-relaxed text-white/55">{copy.text}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

// ─── Phone screens ───────────────────────────────────────────────────────────
const Avatar = ({ name, size = 'h-9 w-9 text-[11px]' }) => (
  <span
    className={`flex shrink-0 items-center justify-center rounded-full border font-bold ${size}`}
    style={{ borderColor: 'rgba(212,175,55,0.5)', color: GOLD, backgroundColor: 'rgba(212,175,55,0.08)' }}
  >
    {(name || '?')[0]}
  </span>
);

const ScreenPeople = ({ t }) => {
  const people = t('experience.screens.people', { returnObjects: true }) || [];
  return (
    <div className="flex h-full flex-col bg-[#0A0A0A] px-4 pt-12" data-testid="landing-people-preview">
      <span className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>
        {t('experience.screens.people_label')}
      </span>
      <p className="mt-2 text-base font-semibold leading-snug text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
        {t('experience.screens.people_title')}
      </p>
      <div className="mt-4 space-y-2.5">
        {people.map((p) => (
          <div key={p.name} className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3">
            <div className="flex items-center gap-2.5">
              <Avatar name={p.name} />
              <div className="min-w-0">
                <p className="text-[11px] font-bold text-white">{p.name}</p>
                <p className="truncate text-[9px] text-white/50">{p.tags}</p>
              </div>
            </div>
            <p className="mt-2 text-[10px] leading-snug text-white/65">{p.bio}</p>
          </div>
        ))}
      </div>
      <div className="mt-auto pb-6">
        <span className="flex h-10 w-full items-center justify-center rounded-full text-[11px] font-bold text-black" style={{ backgroundColor: GOLD }}>
          {t('experience.screens.people_cta')}
        </span>
      </div>
    </div>
  );
};

const ScreenChat = ({ t }) => (
  <div className="flex h-full flex-col bg-[#0A0A0A] px-4 pt-11" data-testid="landing-chat-preview">
    <div className="flex items-center gap-2.5 border-b border-white/5 pb-3">
      <Avatar name={t('experience.screens.chat_name')} />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold text-white">{t('experience.screens.chat_name')}</p>
        <p className="text-[9px] text-emerald-400">{t('experience.screens.chat_status')}</p>
      </div>
      <Heart className="h-4 w-4 fill-current" style={{ color: GOLD }} />
      <PhoneCall className="h-4 w-4 text-white/70" />
    </div>
    <div className="mt-4 space-y-2">
      <p className="w-fit max-w-[85%] rounded-2xl rounded-tl-sm border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-[10px] leading-snug text-white/85">
        {t('experience.screens.chat_1')}
      </p>
      <p className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-tr-sm px-3 py-2 text-[10px] leading-snug text-black" style={{ backgroundColor: GOLD }}>
        {t('experience.screens.chat_2')}
      </p>
      <div className="flex w-fit max-w-[85%] items-center gap-2 rounded-2xl rounded-tl-sm border border-white/[0.08] bg-white/[0.04] px-3 py-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: GOLD }}>
          <Play className="ml-px h-3 w-3 fill-black text-black" />
        </span>
        <span className="flex h-4 items-end gap-[2px]" aria-hidden="true">
          {[6, 12, 16, 9, 14, 7, 15, 10, 12, 6].map((h, i) => (
            <span key={i} className="w-[2px] rounded-full" style={{ height: h, backgroundColor: i < 6 ? GOLD : 'rgba(255,255,255,0.25)' }} />
          ))}
        </span>
        <span className="text-[9px] text-white/50">{t('experience.screens.chat_voice')}</span>
      </div>
      <p className="mx-auto mt-3 flex w-fit items-center gap-1.5 rounded-full border border-white/[0.08] px-2.5 py-1 text-[9px] text-white/50" data-testid="landing-chat-favourite">
        <Heart className="h-3 w-3 fill-current" style={{ color: GOLD }} /> {t('experience.screens.chat_fav')}
      </p>
    </div>
    <div className="mt-auto flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-2">
      <span className="flex-1 text-[10px] text-white/40">{t('experience.screens.chat_placeholder')}</span>
      <Mic className="h-4 w-4" style={{ color: GOLD }} />
    </div>
    <p className="mb-5 mt-2 text-center text-[9px] font-semibold" style={{ color: GOLD }} data-testid="landing-chat-free">
      {t('experience.screens.chat_free')}
    </p>
  </div>
);

const ScreenLive = ({ t }) => (
  <div className="relative flex h-full flex-col items-center bg-[#0A0A0A] px-5 pt-24" data-testid="landing-audio-live-preview">
    <div className="absolute left-3 right-3 top-9 flex items-center justify-between">
      <span className="rounded-md bg-red-600 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-white" data-testid="landing-audio-live-label">
        {t('experience.screens.live_label')}
      </span>
      <span className="rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-semibold text-white" data-testid="landing-audio-live-listeners">{t('experience.screens.listeners')}</span>
    </div>
    <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10">
      <Radio className="h-9 w-9" style={{ color: GOLD }} strokeWidth={1.4} />
    </span>
    <p className="mt-5 text-center text-base font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="landing-audio-live-title">{t('experience.screens.live_title')}</p>
    <div className="mt-5 flex h-9 items-center gap-1" aria-hidden="true">
      {[10, 18, 28, 14, 32, 22, 12, 26, 16, 30, 14].map((height, index) => <span key={index} className="w-1 rounded-full" style={{ height, backgroundColor: GOLD }} />)}
    </div>
    <div className="absolute inset-x-3 bottom-[4.5rem] space-y-1.5">
      <p className="w-fit rounded-full bg-black/50 px-2.5 py-1 text-[9px] text-white backdrop-blur-sm">
        <span className="font-bold" style={{ color: GOLD }}>sara</span> {t('experience.screens.chat_msg_1')}
      </p>
      <p className="w-fit rounded-full bg-black/50 px-2.5 py-1 text-[9px] text-white backdrop-blur-sm">
        <span className="font-bold" style={{ color: GOLD }}>luca_b</span> {t('experience.screens.chat_msg_2')}
      </p>
      <p className="w-fit rounded-full border px-2.5 py-1 text-[9px] font-semibold" style={{ borderColor: 'rgba(212,175,55,0.5)', color: GOLD }} data-testid="landing-audio-live-stage">
        ✋ {t('experience.screens.live_stage')}
      </p>
    </div>
    <div className="absolute inset-x-0 bottom-4 flex items-center justify-center gap-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm"><Mic className="h-4 w-4 text-white" /></span>
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-600"><PhoneCall className="h-4 w-4 text-white" /></span>
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm"><MicOff className="h-4 w-4 text-white" /></span>
    </div>
  </div>
);

const ScreenPro = ({ t }) => (
  <div className="flex h-full flex-col bg-[#0A0A0A] px-4 pt-12" data-testid="landing-pro-preview">
    <span className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>
      {t('experience.screens.pro_label')}
    </span>
    <p className="mt-2 text-base font-semibold leading-snug text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
      {t('experience.screens.pro_title')}
    </p>
    <div className="mt-4 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] p-3">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-3.5 w-3.5" style={{ color: GOLD }} />
        <span className="text-[10px] text-white/70">{t('experience.screens.pro_live_when')}</span>
      </div>
      <p className="mt-1.5 text-[12px] font-semibold text-white">{t('experience.screens.pro_live_title')}</p>
      <p className="mt-1 text-[9px] text-white/50">{t('experience.screens.pro_live_booked')}</p>
    </div>
    <div className="mt-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3">
      <div className="flex items-center gap-2">
        <Heart className="h-3.5 w-3.5 fill-current" style={{ color: GOLD }} />
        <span className="text-[10px] font-bold text-white">{t('experience.screens.pro_tip_title')}</span>
      </div>
      <p className="mt-1.5 text-[10px] leading-snug text-white/65">
        <span className="font-bold" style={{ color: GOLD }}>giulia_r</span> {t('experience.screens.pro_tip_msg')}
      </p>
    </div>
    <div className="mt-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3">
      <div className="flex items-center gap-2">
        <Star className="h-3.5 w-3.5" style={{ color: GOLD }} />
        <span className="text-[10px] font-bold text-white">{t('experience.screens.pro_special_title')}</span>
      </div>
      <p className="mt-1.5 text-[10px] leading-snug text-white/65">{t('experience.screens.pro_special_text')}</p>
    </div>
    <div className="mt-auto pb-6">
      <span className="flex h-10 w-full items-center justify-center rounded-full border text-[11px] font-bold" style={{ borderColor: GOLD, color: GOLD }}>
        {t('experience.screens.pro_cta')}
      </span>
    </div>
  </div>
);

const SCREENS = { people: ScreenPeople, chat: ScreenChat, live: ScreenLive, pro: ScreenPro };

const PhoneMockup = ({ active }) => {
  const { t } = useTranslation('landing');
  const screenKey = LANDING_STRUCTURE.experience.cards[active]?.screen || 'people';
  const Screen = SCREENS[screenKey];
  return (
    <div className="relative mx-auto" data-testid="phone-mockup">
      <div
        className="pointer-events-none absolute -inset-10 rounded-full opacity-60"
        style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.14) 0%, transparent 65%)' }}
      />
      <div className="relative h-[560px] w-[272px] overflow-hidden rounded-[2.8rem] border-[7px] border-neutral-900 bg-black shadow-[0_25px_80px_rgba(0,0,0,0.8),0_0_40px_rgba(212,175,55,0.12)]">
        <div className="absolute left-1/2 top-2.5 z-20 h-5 w-24 -translate-x-1/2 rounded-full bg-black" />
        <AnimatePresence mode="wait">
          <motion.div
            key={screenKey}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: easeCine }}
            className="h-full"
            data-testid={`phone-screen-${screenKey}`}
          >
            <Screen t={t} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// ─── Esperienza (sticky mockup) ──────────────────────────────────────────────
const SectionExperience = ({ onEnter }) => {
  const { t } = useTranslation('landing');
  const cards = t('experience.cards', { returnObjects: true }) || [];
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  // Auto-rotate the phone screens; a manual choice pauses the carousel.
  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => {
      setActive((a) => (a + 1) % LANDING_STRUCTURE.experience.cards.length);
    }, 4200);
    return () => clearInterval(timer);
  }, [paused]);

  const choose = (i) => {
    setActive(i);
    setPaused(true);
  };

  return (
    <section id="esperienza" className="relative overflow-hidden bg-[#050505] py-20 lg:py-24" data-testid="section-experience">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }}>
          <h2 className="text-4xl leading-tight text-white sm:text-5xl" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t('experience.title_1')}
            <br />
            {t('experience.title_2')}
          </h2>
          <span className="mt-4 block h-[2px] w-24 rounded-full" style={{ backgroundColor: GOLD }} />
          <p className="mt-5 max-w-lg text-[15px] text-white/60" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {t('experience.subtitle')}
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">
          {/* Cards */}
          <div className="order-2 space-y-4 lg:order-1">
            {LANDING_STRUCTURE.experience.cards.map((c, i) => {
              const Icon = ICONS[c.icon];
              const copy = cards[i] || {};
              const isActive = active === i;
              return (
                <motion.button
                  key={c.testid}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.4 }}
                  custom={i}
                  onClick={() => choose(i)}
                  className={`block w-full rounded-2xl border p-6 text-left transition-colors duration-300 ${
                    isActive ? 'border-[#D4AF37]/50 bg-white/[0.04]' : 'border-white/5 bg-white/[0.015] hover:bg-white/[0.03]'
                  }`}
                  data-testid={c.testid}
                >
                  <div className="flex items-start gap-4">
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
                        isActive ? 'border-transparent' : 'border-white/15'
                      }`}
                      style={isActive ? { backgroundColor: GOLD, color: '#050505' } : { color: GOLD }}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </span>
                    <div style={{ fontFamily: "'Manrope', sans-serif" }}>
                      <h3 className={`text-base font-bold ${isActive ? 'text-white' : 'text-white/85'}`}>{copy.title}</h3>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-white/55">{copy.text}</p>
                    </div>
                  </div>
                </motion.button>
              );
            })}

            <motion.button
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={4}
              whileTap={{ scale: 0.97 }}
              onClick={onEnter}
              className="mt-4 inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold transition-colors hover:bg-white/[0.04]"
              style={{ borderColor: 'rgba(212,175,55,0.4)', color: GOLD }}
              data-testid="experience-cta"
            >
              {t('hero.cta_primary')} <ArrowRight className="h-4 w-4" />
            </motion.button>
          </div>

          {/* Sticky phone */}
          <div className="order-1 lg:order-2">
            <div className="lg:sticky lg:top-28">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, ease: easeCine }}
              >
                <PhoneMockup active={active} />
                <div className="mt-6 flex justify-center gap-2" data-testid="phone-dots">
                  {LANDING_STRUCTURE.experience.cards.map((c, i) => (
                    <button
                      key={c.testid}
                      onClick={() => choose(i)}
                      aria-label={`screen ${i + 1}`}
                      className="h-1.5 rounded-full transition-all duration-300"
                      style={{
                        width: active === i ? 22 : 6,
                        backgroundColor: active === i ? GOLD : 'rgba(255,255,255,0.2)',
                      }}
                      data-testid={`phone-dot-${i}`}
                    />
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Nymia nella vita di tutti i giorni ──────────────────────────────────────
const SectionDaily = () => {
  const { t } = useTranslation('landing');
  const cols = t('daily.cols', { returnObjects: true }) || [];
  return (
    <section id="quotidiano" className="border-t border-white/5 bg-[#0A0A0A] py-20 lg:py-24" data-testid="section-daily">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }}>
          <h2 className="text-4xl leading-tight text-white sm:text-5xl" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="daily-title">
            {t('daily.title_1')}
            <br />
            {t('daily.title_2')}
          </h2>
          <span className="mt-4 block h-[2px] w-24 rounded-full" style={{ backgroundColor: GOLD }} />
          <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/60" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="daily-intro">
            {t('daily.intro_1')}
            <span className="block text-white/80">{t('daily.intro_2')}</span>
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
          {LANDING_STRUCTURE.daily.cols.map((c, i) => {
            const copy = cols[i] || {};
            return (
              <motion.figure
                key={c.testid}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                custom={i}
                className="group"
                data-testid={c.testid}
              >
                <div className="overflow-hidden rounded-2xl border border-white/5">
                  <img
                    src={IMG[c.imgKey]}
                    alt={copy.title || ''}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
                <figcaption className="mt-5">
                  <h3 className="text-xl leading-snug text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {copy.title}
                  </h3>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-white/60" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {copy.text}
                  </p>
                </figcaption>
              </motion.figure>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── Sicurezza ───────────────────────────────────────────────────────────────
const SectionSafety = () => {
  const { t } = useTranslation('landing');
  const items = t('safety.items', { returnObjects: true }) || [];
  return (
    <section id="sicurezza" className="border-t border-white/5 bg-[#050505] py-16 lg:py-20" data-testid="section-safety">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.5 }}
          className="mx-auto max-w-xl text-center text-2xl leading-snug text-white sm:text-3xl"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {t('safety.title')}
        </motion.h2>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {LANDING_STRUCTURE.safety.items.map((c, i) => {
            const Icon = ICONS[c.icon];
            const copy = items[i] || {};
            return (
              <motion.div
                key={c.testid}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                custom={i}
                className="rounded-2xl border border-white/5 bg-white/[0.02] p-7 text-center backdrop-blur-sm"
                data-testid={c.testid}
              >
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border" style={{ borderColor: 'rgba(212,175,55,0.35)', color: GOLD }}>
                  <Icon className="h-5 w-5" strokeWidth={1.6} />
                </span>
                <h3 className="mt-4 text-[15px] font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>{copy.title}</h3>
                <p className="mt-1.5 text-[13px] text-white/55" style={{ fontFamily: "'Manrope', sans-serif" }}>{copy.text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── CTA finale ──────────────────────────────────────────────────────────────
const FinalCTA = ({ onEnter }) => {
  const { t, i18n } = useTranslation('landing');
  const lang = (i18n.resolvedLanguage || i18n.language || 'it').split('-')[0];
  const badgeSrc = lang === 'en' ? '/badges/google-play-en.png' : '/badges/google-play-it.png';
  return (
    <section className="relative overflow-hidden border-t border-white/5 py-24 lg:py-28" data-testid="section-final-cta">
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-50"
        style={{ background: 'radial-gradient(ellipse, rgba(212,175,55,0.12) 0%, transparent 60%)' }}
      />
      <div className="relative mx-auto max-w-3xl px-5 text-center lg:px-8">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.5 }}
          className="text-3xl leading-snug text-white sm:text-4xl lg:text-5xl"
          style={{ fontFamily: "'Playfair Display', serif" }}
          data-testid="final-cta-title"
        >
          {t('finalCta.title')}
        </motion.h2>
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          custom={1}
          className="mx-auto mt-6 max-w-xl text-[15px] text-white/65"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {t('finalCta.subtitle')}
        </motion.p>
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          custom={2}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={onEnter}
            className="rounded-full px-9 py-4 text-sm font-bold text-black shadow-[0_0_50px_rgba(212,175,55,0.3)] transition-opacity hover:opacity-90"
            style={{ backgroundColor: GOLD }}
            data-testid="final-enter-btn"
          >
            {t('finalCta.primary')}
          </motion.button>
          <button
            onClick={() => scrollToId('esperienza')}
            className="rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-white transition-colors hover:border-white/50"
            data-testid="final-discover-btn"
          >
            {t('finalCta.secondary')}
          </button>
        </motion.div>
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          custom={3}
          className="mt-8 flex justify-center"
        >
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block transition-opacity hover:opacity-90"
            data-testid="google-play-badge"
          >
            <img
              src={badgeSrc}
              alt={t('finalCta.play_badge_alt')}
              width={646}
              height={250}
              className="h-14 w-auto sm:h-16"
              loading="lazy"
            />
          </a>
        </motion.div>
        <p className="mt-5 text-xs text-white/40" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="final-browser-note">
          {t('finalCta.browser_note')}
        </p>
      </div>
    </section>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  useLandingMetadata('home');
  const navigate = useNavigate();
  const onEnter = useCallback(() => {
    trackPixelEvent('Lead', { content_name: 'landing_cta' });
    navigate('/register');
  }, [navigate]);

  useEffect(() => {
    trackPixelEvent('ViewContent', { content_name: 'landing' });
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="landing-page">
      <LandingHeader onEnter={onEnter} />
      <main>
        <Hero onEnter={onEnter} />
        <WhyStrip />
        <SectionExperience onEnter={onEnter} />
        {/* Aperitivo Nymia (gestito da Admin) */}
        <AperitivoSection source="home" />
        <SectionDaily />
        <SectionSafety />
        <FinalCTA onEnter={onEnter} />
      </main>
      <LegalFooter />
      <AndroidPlayBanner />
    </div>
  );
}
